import React, { useEffect, useState } from "react";
import { Client } from "../../interfaces/client";
import { AdditionalContact } from "../../interfaces/additionalContact";
import { DeliveryOption } from "../../interfaces/deliveryOption";
import {
  checkClientHasOrders,
  checkEmailExists,
  checkForms,
  getClientByEmail,
  updateClient,
} from "../../services/clientService";
import { useAuth } from "../../auth";
import "./Profile.css";
import {
  createAdditionalContact,
  deleteAdditionalContact,
  getAdditionalContacts,
  updateAdditionalContact,
} from "../../services/additionalContactService";
import { printClientProfile } from "../../services/clientService";
import { getDeliveryOptions } from "../../services/deliveryOptionsService";
import {
  getStates,
  isValidEmail,
  isValidPhone,
  isValidZip,
} from "../../services/helpers";
import ConfirmDelete from "../../components/component-tools/ConfirmDelete";
import { notifyAdminOfClientUpdate } from "../../services/emailService";
import ToolTip from "../../components/component-tools/ToolTip";
import EmailPreferences from "../../components/client-components/EmailPreferences";
import { ClientNotification } from "../../interfaces/clientNotification";
import { getNotifications } from "../../services/notificationsService";
import NotificationsPopup from "../../components/client-components/NotificationsPopup";

const Profile = () => {
  const states = getStates();
  const [client, setClient] = useState<Client | null>(null);
  const [originalClient, setOriginalClient] = useState<Client | null>(null);
  const [additionalContacts, setAdditionalContacts] = useState<
    AdditionalContact[] | null
  >(null);
  const [originalAdditionalContacts, setOriginalAdditionalContacts] = useState<
    AdditionalContact[] | null
  >(null);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>([]);
  const { decodeToken } = useAuth();
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAbbreviation, setShowAbbreviation] = useState(false);
  const [hasOrders, setHasOrders] = useState(false);
  const [localAdditionalContactIDs, setLocalAdditionalContactIDs] = useState<
    Set<number>
  >(new Set());
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [showEmailPreferencesPopup, setShowEmailPreferencesPopup] =
    useState(false);
  const [notifications, setNotifications] = useState<ClientNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const loadClient = async () => {
      try {
        const email = decodeToken(localStorage.getItem("token") || "");
        const clientIDString = localStorage.getItem("clientID");
        const clientID = clientIDString ? parseInt(clientIDString, 10) : 0;
        const client = await getClientByEmail(email, clientID);
        setClient(client);
        setOriginalClient(client);

        const [showAbbreviationResponse] = await checkForms(client.clientID);
        setShowAbbreviation(showAbbreviationResponse);

        const additionalContacts = await getAdditionalContacts(client.clientID);
        setAdditionalContacts(additionalContacts);
        setOriginalAdditionalContacts(additionalContacts);

        const options = await getDeliveryOptions();
        setDeliveryOptions(options);

        const hasOrdersResponse = await checkClientHasOrders(client.clientID);
        setHasOrders(hasOrdersResponse);

        const notificationsResponse = await getNotifications(client?.clientID);
        notificationsResponse.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        setNotifications(notificationsResponse);

        const unreadCount = notificationsResponse.length;
        setUnreadCount(unreadCount);
      } catch (error) {
        console.error("Error fetching client:", error);
      } finally {
        setLoading(false);
      }
    };

    loadClient();
  }, [decodeToken]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: string,
  ) => {
    setEditing(true);
    const value =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    setClient((prevClient) => ({
      ...prevClient!,
      [field]: value,
    }));

    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  };

  const handleAdditionalContactChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    field: string,
    index: number,
  ) => {
    setEditing(true);
    const { type, value } = e.target;
    const checked =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : undefined;

    setAdditionalContacts(
      (prevContacts) =>
        prevContacts?.map((contact, i) =>
          i === index
            ? {
                ...contact,
                [field]: type === "checkbox" ? checked : value,
              }
            : contact,
        ) || [],
    );

    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [`additionalContact${index}_${field}`]: "",
    }));
  };

  const validateFields = async () => {
    const errors: { [key: string]: string } = {};

    if (!client?.clientName)
      errors.clientName = "Organization Name is required.";
    if (!client?.phoneNumber) {
      errors.phoneNumber = "Phone Number is required.";
    } else if (!isValidPhone(client?.phoneNumber)) {
      errors.phoneNumber = "Please enter a valid phone number.";
    }
    if (client?.fax && !isValidPhone(client.fax)) {
      errors.fax = "Please enter a valid fax number.";
    }
    if (!client?.address) errors.address = "Address is required.";
    if (!client?.city) errors.city = "City is required.";
    if (!client?.state) errors.state = "State is required.";
    if (!client?.zipCode) {
      errors.zipCode = "Zip Code is required.";
    } else if (!isValidZip(client?.zipCode)) {
      errors.zipCode = "Please enter a valid zip code.";
    }
    if (!client?.primaryFirstName)
      errors.primaryFirstName = "First Name is required.";
    if (!client?.primaryLastName)
      errors.primaryLastName = "Last Name is required.";
    if (!client?.primaryEmail)
      errors.primaryEmail = "Primary Email is required.";
    else if (!isValidEmail(client.primaryEmail))
      errors.primaryEmail = "Please enter a valid email address.";

    // Primary email check
    const emailChanged = originalClient?.primaryEmail !== client?.primaryEmail;

    if (
      emailChanged &&
      client?.primaryEmail &&
      (await checkEmailExists(client.primaryEmail, client.clientID))
    ) {
      errors.primaryEmail = "Email already in use.";
    }

    // Additional contacts validation
    if (additionalContacts) {
      for (let index = 0; index < additionalContacts.length; index++) {
        const contact = additionalContacts[index];

        // Check if Primary Email and Contact Email are the same
        if (client?.primaryEmail === contact.contactEmail) {
          errors[`additionalContact${index}_contactEmail`] =
            "Primary Email and Contact Email cannot be the same.";
          errors.primaryEmail =
            "Primary Email and Contact Email cannot be the same.";
        }

        // Check for duplicate emails within additional contacts
        for (
          let otherIndex = 0;
          otherIndex < additionalContacts.length;
          otherIndex++
        ) {
          if (
            otherIndex !== index &&
            contact.contactEmail === additionalContacts[otherIndex].contactEmail
          ) {
            errors[`additionalContact${index}_contactEmail`] =
              "Contact email must be unique across all contacts.";
          }
        }

        // Check for duplicate emails for each additional contact
        const originalContact = originalAdditionalContacts?.find(
          (c) => c.additionalContactID === contact.additionalContactID,
        );

        const emailChanged =
          originalContact &&
          originalContact.contactEmail !== contact.contactEmail;

        if (
          emailChanged &&
          client?.primaryEmail &&
          (await checkEmailExists(contact.contactEmail, client.clientID))
        ) {
          errors[`additionalContact${index}_contactEmail`] =
            "Email is already in use.";
        }

        if (!contact.contactFirstName)
          errors[`additionalContact${index}_contactFirstName`] =
            "First Name is required.";
        if (!contact.contactLastName)
          errors[`additionalContact${index}_contactLastName`] =
            "Last Name is required.";
        if (!contact.contactEmail) {
          errors[`additionalContact${index}_contactEmail`] =
            "Contact Email is required.";
        } else if (!isValidEmail(contact.contactEmail)) {
          errors[`additionalContact${index}_contactEmail`] =
            "Please enter a valid email address.";
        }
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    try {
      setLoadingSpinner(true);
      if (client && (await validateFields())) {
        await updateClient(client.clientID, client);

        if (additionalContacts) {
          for (const contact of additionalContacts) {
            if (localAdditionalContactIDs.has(contact.additionalContactID)) {
              await createAdditionalContact(contact);
            } else {
              await updateAdditionalContact(contact);
            }
          }
        }
        //send email to admin
        await notifyAdminOfClientUpdate(
          client.clientID,
          "Profile Information or Additional Contacts",
        );
        setEditing(false);
        setLoadingSpinner(false);
        setSuccessMessage("Profile information saved successfully.");
      }
    } catch (error) {
      console.error("Error saving profile information:", error);
      setSuccessMessage("");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleDelete = async (contactID: number) => {
    try {
      setLoading(true);

      if (localAdditionalContactIDs.has(contactID)) {
        setAdditionalContacts((prevContacts) =>
          prevContacts
            ? prevContacts.filter(
                (contact) => contact.additionalContactID !== contactID,
              )
            : [],
        );

        setLocalAdditionalContactIDs((prevIDs) => {
          const updatedIDs = new Set(prevIDs);
          updatedIDs.delete(contactID);
          return updatedIDs;
        });
        setSuccessMessage("Unsaved contact removed.");
      } else {
        await deleteAdditionalContact(contactID);

        setAdditionalContacts((prevContacts) =>
          prevContacts
            ? prevContacts.filter(
                (contact) => contact.additionalContactID !== contactID,
              )
            : null,
        );
        setSuccessMessage("Contact Deleted.");
      }
    } catch (error) {
      console.error("Error deleting contact:", error);
      setSuccessMessage("");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContact = () => {
    setEditing(true);
    const newContact: AdditionalContact = {
      additionalContactID: Date.now() % 2147483647,
      clientID: client?.clientID || 0,
      contactFirstName: "",
      contactLastName: "",
      contactEmail: "",
      contactTitle: "",
      contactJobTitle: "",
      invoiceManager: false,
    };

    setAdditionalContacts((prevContacts) => [
      ...(prevContacts || []),
      newContact,
    ]);
    setLocalAdditionalContactIDs((prevIDs) =>
      new Set(prevIDs).add(newContact.additionalContactID),
    );

    const index = additionalContacts ? additionalContacts.length : 0;
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [`additionalContact${index}_contactFirstName`]: "First Name is required.",
      [`additionalContact${index}_contactLastName`]: "Last Name is required.",
      [`additionalContact${index}_contactEmail`]: "Email is required.",
    }));
  };

  const handlePrintProfile = async () => {
    try {
      if (!client || !validateFields()) {
        setSuccessMessage("Please fix validation errors before printing.");
        return;
      }

      setLoading(true);

      const pdfBlob = await printClientProfile(
        client.clientID,
        showAbbreviation,
      );

      if (pdfBlob) {
        const url = window.URL.createObjectURL(pdfBlob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "ClientProfile.pdf");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setLoading(false);
      } else {
        setSuccessMessage("Failed to generate PDF.");
      }
    } catch (error) {
      setLoading(false);
      console.error("Error generating profile PDF:", error);
      setSuccessMessage("An error occurred while generating the PDF.");
    }
  };

  return (
    <div className="content-container">
      <div className="header-container">
        <h1>Profile</h1>
        <div className="page-items-container">
          <div className="field-long">
            <label className="field-label">Client Name:</label>
            <div>
              <input
                className="form-control"
                type="text"
                value={client?.clientName || ""}
                disabled
                readOnly
              />
            </div>
          </div>
          <div className="field-long" style={{ flex: "0 0 40%" }}>
            <label className="field-label">Abbreviation/Client Number:</label>
            <div>
              <input
                className="form-control"
                type="text"
                value={
                  showAbbreviation
                    ? client?.abbreviation || ""
                    : "not available."
                }
                disabled
                readOnly
              />
            </div>
          </div>
          <button
            className="notification-button"
            onClick={() => setShowNotifications(true)}
          >
            <i className="bi bi-bell-fill"></i>
            {unreadCount > 0 && (
              <span className="notification-dot">{unreadCount}</span>
            )}
          </button>
        </div>
        {showNotifications && client?.clientID && (
          <NotificationsPopup
            notificationsInput={notifications}
            clientID={client?.clientID}
            onClose={(
              newUnreadCount = unreadCount,
              newNotifications = notifications,
            ) => {
              setNotifications(newNotifications);
              setShowNotifications(false);
              setUnreadCount(newUnreadCount);
            }}
          />
        )}
      </div>

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          {!showAbbreviation && (
            <div className="text-container error">
              Please complete necessary forms in order to start ordering
              background screening and referencing. Then your
              abbreviation/client number will become visible.{" "}
            </div>
          )}

          <div className="data-container">
            <div
              className="text-header"
              style={{
                flex: "0 0 100%",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              Profile Information
              <div style={{ display: "flex", flexDirection: "row" }}>
                <div>
                  <button
                    onClick={() => handlePrintProfile()}
                    className="oxbtn btn-light"
                    style={{ marginLeft: "10px" }}
                  >
                    Print Profile{" "}
                    <i className="bi bi-printer-fill btn-icon"></i>
                  </button>
                </div>
                <div>
                  <button
                    onClick={() =>
                      setShowEmailPreferencesPopup(!showEmailPreferencesPopup)
                    }
                    className="oxbtn btn-grey"
                    style={{ marginLeft: "10px" }}
                  >
                    Email Notifications{" "}
                    <i className="bi bi-gear-fill btn-icon"></i>
                  </button>
                </div>
              </div>
            </div>
            {showEmailPreferencesPopup && client && (
              <EmailPreferences
                client={client}
                onClose={() => setShowEmailPreferencesPopup(false)}
                onFrequencyChange={(newFreqID) => {
                  setClient((prev) =>
                    prev ? { ...prev, emailFrequencyID: newFreqID } : prev,
                  );
                }}
              />
            )}

            <div className="field-stacked" style={{ flex: "0 0 40%" }}>
              <label className="field-label">Organization Name:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.clientName ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={client?.clientName || ""}
                  onChange={(e) => handleInputChange(e, "clientName")}
                />
                {validationErrors.clientName && (
                  <div className="invalid-feedback">
                    {validationErrors.clientName}
                  </div>
                )}
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Phone Number:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.phoneNumber ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={client?.phoneNumber || ""}
                  onChange={(e) => handleInputChange(e, "phoneNumber")}
                />
                {validationErrors.phoneNumber && (
                  <div className="invalid-feedback">
                    {validationErrors.phoneNumber}
                  </div>
                )}
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <div style={{ display: "flex", gap: "5px" }}>
                <label className="field-label">Confidential Fax:</label>
                <ToolTip
                  tooltipText="We will not call before faxing, reports are faxed upon completion without notification"
                  placement="top"
                  className="info-icon-spec"
                />
              </div>
              <div className="field-input">
                <input
                  className="form-control"
                  type="text"
                  value={client?.fax || ""}
                  onChange={(e) => handleInputChange(e, "fax")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Address:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.address ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={client?.address || ""}
                  onChange={(e) => handleInputChange(e, "address")}
                />
                {validationErrors.address && (
                  <div className="invalid-feedback">
                    {validationErrors.address}
                  </div>
                )}
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 15%" }}>
              <label className="field-label">Address 2:</label>
              <div className="field-input">
                <input
                  className={`form-control`}
                  type="text"
                  value={client?.address2 || ""}
                  onChange={(e) => handleInputChange(e, "address2")}
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">City:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.city ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={client?.city || ""}
                  onChange={(e) => handleInputChange(e, "city")}
                />
                {validationErrors.city && (
                  <div className="invalid-feedback">
                    {validationErrors.city}
                  </div>
                )}
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">State:</label>
              <div className="field-input">
                <select
                  className={`form-select ${
                    validationErrors.state ? "is-invalid" : ""
                  }`}
                  value={client?.state || ""}
                  onChange={(e) => handleInputChange(e, "state")}
                >
                  <option value="">Select State</option>
                  {states.map((state) => (
                    <option key={state.abbreviation} value={state.abbreviation}>
                      {state.name} ({state.abbreviation})
                    </option>
                  ))}
                </select>
                {validationErrors.state && (
                  <div className="invalid-feedback">
                    {validationErrors.state}
                  </div>
                )}
              </div>
            </div>
            {client?.state === "INT" && (
              <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                <label className="field-label">Country:</label>
                <div className="field-input">
                  <input
                    className="form-control"
                    type="text"
                    value={client.country ? client.country : ""}
                    onChange={(e) => handleInputChange(e, "country")}
                  />
                </div>
              </div>
            )}
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Zip Code:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.zipCode ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={client?.zipCode || ""}
                  onChange={(e) => handleInputChange(e, "zipCode")}
                />
                {validationErrors.zipCode && (
                  <div className="invalid-feedback">
                    {validationErrors.zipCode}
                  </div>
                )}
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Final Report Delivery:</label>
              <div
                style={{
                  flex: "0 0 30%",
                  alignSelf: "end",
                  display: "flex",
                  flexDirection: "row",
                  gap: "5px",
                }}
              >
                <select
                  className={`form-select ${
                    validationErrors.deliveryOptionID ? "is-invalid" : ""
                  }`}
                  value={client?.deliveryOptionID || ""}
                  onChange={(e) => handleInputChange(e, "deliveryOptionID")}
                >
                  {deliveryOptions.map((option) => (
                    <option
                      key={option.deliveryOptionID}
                      value={option.deliveryOptionID}
                    >
                      {option.description}
                    </option>
                  ))}
                </select>
                <ToolTip
                  tooltipText="We will not call before faxing, reports are faxed upon completion without notification"
                  placement="top"
                  className="info-icon"
                />
                {validationErrors.deliveryOptionID && (
                  <div className="invalid-feedback">
                    {validationErrors.deliveryOptionID}
                  </div>
                )}
              </div>
            </div>

            <div className="text-sub-header" style={{ flex: "0 0 100%" }}>
              Primary Login/Contact Information *
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">Title:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.primaryTitle ? "is-invalid" : ""
                  }`}
                  type="text"
                  placeholder="Mr./Ms., The Rev., ..."
                  value={client?.primaryTitle || ""}
                  onChange={(e) => handleInputChange(e, "primaryTitle")}
                />
                {validationErrors.primaryTitle && (
                  <div className="invalid-feedback">
                    {validationErrors.primaryTitle}
                  </div>
                )}
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 20%" }}>
              <label className="field-label">First Name:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.primaryFirstName ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={client?.primaryFirstName || ""}
                  onChange={(e) => handleInputChange(e, "primaryFirstName")}
                />
                {validationErrors.primaryFirstName && (
                  <div className="invalid-feedback">
                    {validationErrors.primaryFirstName}
                  </div>
                )}
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 25%" }}>
              <label className="field-label">Last Name:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.primaryLastName ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={client?.primaryLastName || ""}
                  onChange={(e) => handleInputChange(e, "primaryLastName")}
                />
                {validationErrors.primaryLastName && (
                  <div className="invalid-feedback">
                    {validationErrors.primaryLastName}
                  </div>
                )}
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 25%" }}></div>
            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Job Title:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.primaryJobTitle ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={client?.primaryJobTitle || ""}
                  onChange={(e) => handleInputChange(e, "primaryJobTitle")}
                />
                {validationErrors.primaryJobTitle && (
                  <div className="invalid-feedback">
                    {validationErrors.primaryJobTitle}
                  </div>
                )}
              </div>
            </div>

            <div className="field-stacked" style={{ flex: "0 0 30%" }}>
              <label className="field-label">Email:</label>
              <div className="field-input">
                <input
                  className={`form-control ${
                    validationErrors.primaryEmail ? "is-invalid" : ""
                  }`}
                  type="text"
                  value={client?.primaryEmail || ""}
                  onChange={(e) => handleInputChange(e, "primaryEmail")}
                />
                {validationErrors.primaryEmail && (
                  <div className="invalid-feedback">
                    {validationErrors.primaryEmail}
                  </div>
                )}
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 10%" }}>
              <label className="field-label">Invoice Manager:</label>
              <div>
                <input
                  className={`form-check-input`}
                  type="checkbox"
                  checked={client?.primaryInvoiceManager || false}
                  onChange={(e) =>
                    handleInputChange(e, "primaryInvoiceManager")
                  }
                />
              </div>
            </div>
            <div className="field-stacked" style={{ flex: "0 0 10%" }}>
              <label className="field-label">
                Receive Email Notifications:
              </label>
              <div>
                <input
                  className={`form-check-input`}
                  type="checkbox"
                  checked={client?.emailNotifications || false}
                  onChange={(e) => handleInputChange(e, "emailNotifications")}
                />
              </div>
            </div>
            <div className="error">
              *All communications from Oxford Document Management will be sent
              to the attention of the primary contact
            </div>
            <div className="text-sub-header" style={{ flex: "0 0 100%" }}>
              Additional Contacts
            </div>
            {additionalContacts?.map((contact, index) => (
              <div
                key={contact.additionalContactID}
                className="additional-contact-group"
              >
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">Title:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Mr./Ms., The Rev., ..."
                      value={contact.contactTitle || ""}
                      onChange={(e) =>
                        handleAdditionalContactChange(e, "contactTitle", index)
                      }
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 20%" }}>
                  <label className="field-label">First Name:</label>
                  <div className="field-input">
                    <input
                      className={`form-control ${
                        validationErrors[
                          `additionalContact${index}_contactFirstName`
                        ]
                          ? "is-invalid"
                          : ""
                      }`}
                      type="text"
                      value={contact.contactFirstName || ""}
                      onChange={(e) =>
                        handleAdditionalContactChange(
                          e,
                          "contactFirstName",
                          index,
                        )
                      }
                    />

                    {validationErrors[
                      `additionalContact${index}_contactFirstName`
                    ] && (
                      <div className="invalid-feedback">
                        {
                          validationErrors[
                            `additionalContact${index}_contactFirstName`
                          ]
                        }
                      </div>
                    )}
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 25%" }}>
                  <label className="field-label">Last Name:</label>
                  <div className="field-input">
                    <input
                      className={`form-control ${
                        validationErrors[
                          `additionalContact${index}_contactLastName`
                        ]
                          ? "is-invalid"
                          : ""
                      }`}
                      type="text"
                      value={contact.contactLastName || ""}
                      onChange={(e) =>
                        handleAdditionalContactChange(
                          e,
                          "contactLastName",
                          index,
                        )
                      }
                    />

                    {validationErrors[
                      `additionalContact${index}_contactLastName`
                    ] && (
                      <div className="invalid-feedback">
                        {
                          validationErrors[
                            `additionalContact${index}_contactLastName`
                          ]
                        }
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className="field-stacked"
                  style={{ flex: "0 0 25%" }}
                ></div>
                <div className="field-stacked" style={{ flex: "0 0 30%" }}>
                  <label className="field-label">Job Title:</label>
                  <div className="field-input">
                    <input
                      className="form-control"
                      type="text"
                      value={contact.contactJobTitle || ""}
                      onChange={(e) =>
                        handleAdditionalContactChange(
                          e,
                          "contactJobTitle",
                          index,
                        )
                      }
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 30%" }}>
                  <label className="field-label">Email:</label>
                  <div className="field-input">
                    <input
                      className={`form-control ${
                        validationErrors[
                          `additionalContact${index}_contactEmail`
                        ]
                          ? "is-invalid"
                          : ""
                      }`}
                      type="text"
                      value={contact.contactEmail || ""}
                      onChange={(e) =>
                        handleAdditionalContactChange(e, "contactEmail", index)
                      }
                    />

                    {validationErrors[
                      `additionalContact${index}_contactEmail`
                    ] && (
                      <div className="invalid-feedback">
                        {
                          validationErrors[
                            `additionalContact${index}_contactEmail`
                          ]
                        }
                      </div>
                    )}
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                  <label className="field-label">Invoice Manager:</label>
                  <div>
                    <input
                      className={`form-check-input`}
                      type="checkbox"
                      checked={contact.invoiceManager || false}
                      onChange={(e) =>
                        handleAdditionalContactChange(
                          e,
                          "invoiceManager",
                          index,
                        )
                      }
                    />
                  </div>
                </div>
                <div className="field-stacked" style={{ flex: "0 0 10%" }}>
                  <label className="field-label">
                    Receive Email Notifications:
                  </label>
                  <div>
                    <input
                      className={`form-check-input`}
                      type="checkbox"
                      checked={contact.emailNotifications || false}
                      onChange={(e) =>
                        handleAdditionalContactChange(
                          e,
                          "emailNotifications",
                          index,
                        )
                      }
                    />
                  </div>
                </div>
                <div style={{ flex: "0 0 100%" }}>
                  <button
                    className="smbtn btn-red"
                    onClick={() => setDeleteId(contact.additionalContactID)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            {deleteId !== null && (
              <ConfirmDelete
                onConfirm={() => {
                  handleDelete(deleteId);
                  setDeleteId(null);
                }}
                onCancel={() => setDeleteId(null)}
                message="Are you sure you want to delete this additional contact?"
              />
            )}

            <div className="btn-center" style={{ flex: "0 0 100%" }}>
              <button
                className="oxbtn btn-purple"
                onClick={() => handleCreateContact()}
              >
                Add Additional Contact
                <i className="bi bi-plus-lg btn-icon"></i>
              </button>
            </div>
            <div className="text-header" style={{ flex: "0 0 100%" }}>
              Invoicing Information
            </div>
            {!hasOrders && (
              <div style={{ color: "red", fontSize: "large" }}>
                A one time $50 set up fee will be billed when the first
                background check is initiated using your unique abbreviation.
              </div>
            )}

            <div className="text-body">
              Clients are invoiced on or about the 1st of each month for
              services commenced the prior month.
            </div>
          </div>
          <div className="btn-center">
            <button
              className={`oxbtn btn-green ${!editing ? "disabled" : ""}`}
              onClick={() => handleSave()}
              disabled={!editing}
            >
              Save
            </button>
          </div>
          {successMessage && (
            <div className="alert-message alert alert-success alert-dismissible fade show">
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage("")}
                aria-label="Close"
              ></button>
            </div>
          )}
          {loadingSpinner && (
            <div className="loading-mask">
              <div className="loading-spinner"></div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Profile;
